/*  ========================================================================
    # Sections - Testimonials
    ========================================================================  */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import tailwindConfig from '../../../../tailwind.config';
import useLocalization from '@/react/hooks/useLocalization';
import useTranslation from '@/react/hooks/useTranslation';
import LazyImage from '@/react/components/LazyImage';
import LazySource from '@/react/components/LazySource';

const Testimonials = ({ headline, entries, mode, leftTestimonial, image, rightTestimonial }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const __ = useTranslation();

  // Icons
  const localize = useLocalization();
  const iconChevronLeft = `${localize('svgSprite')}#angle-left`;
  const iconChevronRight = `${localize('svgSprite')}#angle-right`;

  // If there are fewer than 5 entries, the array is padded with existing ones.
  const sliderEntries = useMemo(() => {
    let tempEntries = [];

    if (mode === 'double')
      tempEntries = [leftTestimonial, rightTestimonial];
    else if (mode === 'simple') {
      if (entries.length > 2 && entries.length < 5) {
        tempEntries = [...entries];
        while (tempEntries.length < 5) tempEntries.push(...entries);
      } else {
        tempEntries = [...entries];
      }
    }

    return tempEntries;
  }, [entries]);

  // Get the slide offset value.
  const getSlideOffset = index => {
    const delta = index - activeSlide;

    if (activeSlide === 0 && index === sliderEntries.length - 1) return -1;
    if (activeSlide === 0 && index === sliderEntries.length - 2) return -2;
    if (activeSlide === 1 && index === sliderEntries.length - 1) return -2;

    if (activeSlide === sliderEntries.length - 1 && index === 0) return 1;
    if (activeSlide === sliderEntries.length - 1 && index === 1) return 2;

    if (activeSlide === sliderEntries.length - 2 && index === 0) return 2;

    if (delta <= -3) return -2;
    if (delta >= 3) return 2;

    return delta;
  };

  // Navigate Left
  const navigateLeft = () => {
    if (activeSlide > 0) setActiveSlide(activeSlide - 1);
    else setActiveSlide(sliderEntries.length - 1);
  };

  // Navigate Right
  const navigateRight = () => {
    if (activeSlide < sliderEntries.length - 1) setActiveSlide(activeSlide + 1);
    else setActiveSlide(0);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: navigateRight,
    onSwipedRight: navigateLeft
  });

  return (
    <>
      <div className="container">
        {headline && <div className='s-testimonials__headline wysiwyg-block' dangerouslySetInnerHTML={{ __html: headline }}></div>}
      </div>
      <div className='container'>
        <div className="row">
          {mode == 'double' ? (
            <>
              <div className="col tw-w-full md:tw-w-8/12 md:tw-ml-[calc(2/12*100%)] xl:tw-w-4/12 xl:tw-ml-0">
                <div className="s-testimonials__doublequote tw-mb-10 xl:tw-mb-0">
                  <div className='s-testimonials__quote-quote'>&ldquo;</div>
                  <div className='s-testimonials__quote-text'>{leftTestimonial.quote}</div>
                  <div className='s-testimonials__quote-name'>{leftTestimonial.name}</div>
                  <div className='s-testimonials__quote-position'>{leftTestimonial.position}</div>
                </div>
              </div>
              <div className="col tw-w-10/12 tw-ml-[calc(1/12*100%)] md:tw-w-6/12 md:tw-ml-[calc(3/12*100%)] lg:tw-w-4/12 lg:tw-ml-[calc(4/12*100%)] xl:tw-w-4/12 xl:tw-ml-0 tw-flex tw-items-center">
                <div className="s-testimonials__doublequote-image-filter tw-my-10 xl:tw-my-0">
                  <div className='s-testimonials__doublequote-image-container'>
                    <LazyImage
                      figureClass={'s-testimonials__doublequote-image'}
                      defaultUrl={image.sizes['medium_large']}
                      alt={image.alt}
                      placeholderUrl={image.sizes['placeholder']}
                      dataOffset={0}
                    >
                      <LazySource srcSet={image.large} mediaMin={tailwindConfig.theme.screens.md} />
                      <LazySource srcSet={image.medium_large} mediaMin={tailwindConfig.theme.screens.sm} />
                    </LazyImage>
                  </div>
                </div>
              </div>
              <div className="col tw-w-full md:tw-w-8/12 md:tw-ml-[calc(2/12*100%)] xl:tw-w-4/12 xl:tw-ml-0">
                <div className="s-testimonials__doublequote">
                  <div className='s-testimonials__quote-quote'>&ldquo;</div>
                  <div className='s-testimonials__quote-text'>{rightTestimonial.quote}</div>
                  <div className='s-testimonials__quote-name'>{rightTestimonial.name}</div>
                  <div className='s-testimonials__quote-position'>{rightTestimonial.position}</div>
                </div>
              </div>
            </>
          ) : null}
        </div>
        <div className='row'>
          {sliderEntries.length !== 2 ? (
            <div className='col tw-w-full lg:tw-w-10/12 lg:tw-ml-[calc(2/12*100%)]'>
              <div className='s-testimonials__quote-container' {...swipeHandlers}>
                {sliderEntries.map((entry, index) => (
                  <div
                    key={`${entry.name}-${index}`}
                    className='s-testimonials__quote'
                    style={sliderEntries.length === 1 ? { opacity: 1 } : {}}
                    data-offset={sliderEntries.length === 1 ? '' : getSlideOffset(index)}
                  >
                    <div className='s-testimonials__quote-quote'>&ldquo;</div>
                    <div className='s-testimonials__quote-text'>{entry.quote}</div>
                    <div className='s-testimonials__quote-name'>{entry.name}</div>
                    <div className='s-testimonials__quote-position'>{entry.position}</div>
                  </div>
                ))}
                {sliderEntries.length > 1 && (
                  <div className='s-testimonials__quote-control-container'>
                    <button
                      className='s-testimonials__quote-control s-testimonials__quote-control--left c-button c-button--icon'
                      title={__('section-testimonials-navigate-left')}
                      aria-label={__('section-testimonials-navigate-left')}
                      onClick={navigateLeft}
                    >
                      <svg viewBox='0 0 200 200'>
                        <use href={iconChevronLeft} />
                      </svg>
                    </button>
                    <button
                      className='s-testimonials__quote-control s-testimonials__quote-control--right c-button c-button--icon'
                      title={__('section-testimonials-navigate-right')}
                      aria-label={__('section-testimonials-navigate-right')}
                      onClick={navigateRight}
                    >
                      <svg viewBox='0 0 200 200'>
                        <use href={iconChevronRight} />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
        {sliderEntries.length !== 2 ? (
          <div className='s-testimonials__quote-image-filter'>
            <div className='s-testimonials__quote-image-container'>
              {sliderEntries.map((entry, index) => (
                <LazyImage
                  key={`${entry.name}-${index}`}
                  figureClass={'s-testimonials__quote-image'}
                  defaultUrl={entry.image.sizes['medium_large']}
                  alt={entry.image.alt}
                  placeholderUrl={entry.image.sizes['placeholder']}
                  dataOffset={sliderEntries.length === 1 ? '' : getSlideOffset(index)}
                >
                  <LazySource srcSet={entry.image.large} mediaMin={tailwindConfig.theme.screens.md} />
                  <LazySource srcSet={entry.image.medium_large} mediaMin={tailwindConfig.theme.screens.sm} />
                </LazyImage>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

Testimonials.propTypes = {
  headline: PropTypes.string,
  entries: PropTypes.array,
  mode: PropTypes.string,
  leftTestimonial: PropTypes.object,
  image: PropTypes.array,
  rightTestimonial: PropTypes.object
};

export default Testimonials;
